import { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { faFlask } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Information } from "@/components/Information/Information";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";

import { SegmentChangeItem } from "../../../KnowledgeDb/schema/knowledgeDbApi";
import { filterIcpChanges, ModalSegmentSync } from "../../components/ModalSegmentSync";
import { ScoreResponse } from "../../schema/score.schema";
import { DeleteRowModal } from "../../shell/DeleteRowModal";
import { focusMatches } from "../../shell/focusTarget";
import { IssueBar } from "../../shell/IssueBar";
import { ScoreLeaveGuard } from "../../shell/ScoreLeaveGuard";
import { ScoreSaveBar } from "../../shell/ScoreSaveBar";
import { ScoreShell } from "../../shell/ScoreShell";
import { SettingsBox } from "../../shell/SettingsBox";
import { AddChip, ChipStatus, StatusChip } from "../../shell/StatusChip";
import { PersonaView, SegmentView } from "../../shell/scoreConfigView";
import { computeScoreIssues, issuesFor, issuesForStep, ScoreIssue } from "../../shell/scoreIssues";
import { saveBlockerText } from "../../shell/saveBlocker";
import { useConfigDraft } from "../../shell/useConfigDraft";
import { useSaveBlockerFocus } from "../../shell/useSaveBlockerFocus";
import { useFocusTarget } from "../../shell/useFocusTarget";
import { useScoreConfig } from "../../shell/useScoreConfig";
import { createScoringHref, editScoringHref } from "../ScoreOverView/ScoreOverview";

import { PersonaDetail } from "./PersonaDetail";
import { PersonaPreview } from "./PersonaPreview";
import { PersonaTestModal } from "./PersonaTestModal";

const HELP_URL = "https://help.signalist.io/personas";

/**
 * Persona Segments (PREC-4055): the people the user sells to, as a chip row
 * with one detail card open at a time — the mirror of the segment page.
 */
export const ScoreProspectCategory: FunctionComponent = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { score, criteria, refetch } = useScoreConfig();
	const { target, clear: clearFocus } = useFocusTarget();
	const isKnowledgeDbActive = useIsFeatureActive(FeaturesCodes.KNOWLEDGE_DB);

	// null = nothing chosen yet (the first persona opens); 0 = explicitly collapsed.
	const [selectedId, setSelectedId] = useState<number | null>(null);
	const [deleting, setDeleting] = useState<PersonaView | null>(null);
	const [testing, setTesting] = useState(false);
	const [syncChanges, setSyncChanges] = useState<SegmentChangeItem[]>([]);

	// The Knowledge DB may answer a save with ICP changes to review (carried over from the old page).
	const onSaved = useCallback(
		(saved: ScoreResponse) => {
			if (isKnowledgeDbActive && saved.knowledgeDbChanges?.hasChanges) {
				const changes = filterIcpChanges(
					(saved.knowledgeDbChanges.changes ?? []) as SegmentChangeItem[]
				);

				if (changes.length > 0) {
					setSyncChanges(changes);
				}
			}
		},
		[isKnowledgeDbActive]
	);

	const edits = useConfigDraft(
		score,
		criteria,
		refetch,
		{ saveError: t("leadScoring.saveError"), blocked: (blocker) => saveBlockerText(blocker, t) },
		onSaved
	);

	// A refused save selects and flashes the row it names (PREC-4987).
	useSaveBlockerFocus(edits.blocker);

	const issues = useMemo(
		() => issuesForStep(computeScoreIssues({ score: edits.draft, criteria }), "prospect"),
		[edits.draft, criteria]
	);

	useEffect(() => {
		if (target?.kind === "persona") {
			setSelectedId(Number(target.id));
		}
	}, [target]);

	const draftReady = !!edits.draft;

	useEffect(() => {
		if (searchParams.get("add") === "persona" && draftReady) {
			setSelectedId(edits.addPersona());
			setSearchParams(
				(current) => {
					const next = new URLSearchParams(current);

					next.delete("add");

					return next;
				},
				{ replace: true }
			);
		}
		// Runs once the draft exists; the param is consumed on that run.
	}, [searchParams, draftReady]);

	const statusOf = (persona: PersonaView): ChipStatus =>
		!persona.active
			? "off"
			: issuesFor(issues, "persona", `${persona.id}`).length > 0
				? "bad"
				: "ok";

	const previewOf = (persona: PersonaView) => (
		<PersonaPreview
			criteria={criteria}
			issues={issuesFor(issues, "persona", `${persona.id}`)}
			persona={persona}
			rows={edits.segments
				.filter((segment) => segment.active && edits.isLinked(segment, persona))
				.map((segment) => ({ segment, assignment: edits.assignmentOf(segment, persona) }))}
		/>
	);

	const selectIssue = (issue: ScoreIssue) => setSelectedId(Number(issue.id));

	const cellActions = {
		onPick: edits.pick,
		onClear: edits.clear,
		onDeactivate: (segment: SegmentView, persona: PersonaView) =>
			edits.setLinked(segment, persona, false),
		onCreate: (segment: SegmentView, persona: PersonaView) =>
			navigate(createScoringHref(segment, persona)),
		onEdit: (criteriaId: number) => navigate(editScoringHref(criteriaId)),
	};

	const openId = selectedId === null ? (edits.personas[0]?.id ?? null) : selectedId || null;
	const selected = edits.personas.find((persona) => persona.id === openId);
	const liveCount = edits.personas.filter((persona) => persona.active).length;

	const runTest = async () => {
		if (!edits.dirty || (await edits.save())) {
			setTesting(true);
		}
	};

	return (
		<ScoreShell
			helpUrl={HELP_URL}
			intro={t("leadScoring.personas.intro")}
			step="prospect"
			introActions={
				<button
					className="flex h-7 cursor-pointer items-center gap-1.5 rounded-lg border border-border-light bg-white px-2.5 text-xs font-medium text-text hover:bg-background-light"
					data-testid="personas-test"
					type="button"
					onClick={runTest}
				>
					<Icon className="h-3 w-3" icon={faFlask} />
					{t("leadScoring.personas.test")}
				</button>
			}
		>
			<IssueBar
				issues={issues}
				subject={t("leadScoring.personas.subject")}
				onSelect={selectIssue}
			/>

			<SettingsBox
				actions={<Information information={t("leadScoring.personas.countInfo")} />}
				testId="persona-box"
				title={t("leadScoring.personas.count", { count: liveCount })}
			>
				<div className="flex flex-wrap items-center gap-1.5">
					{edits.personas.map((persona) => (
						<StatusChip
							key={persona.id}
							label={persona.name || t("leadScoring.shell.untitled")}
							preview={previewOf(persona)}
							selected={openId === persona.id}
							status={statusOf(persona)}
							testId={`persona-chip-${persona.id}`}
							onClick={() => setSelectedId(openId === persona.id ? 0 : persona.id)}
						/>
					))}
					<AddChip
						label={t("leadScoring.personas.add")}
						testId="persona-add"
						onClick={() => setSelectedId(edits.addPersona())}
					/>
				</div>
				{selected && (
					// Keyed per persona: the example-title autocomplete seeds its state once and
					// writes it back, so a reused instance shows the previous persona's titles.
					<PersonaDetail
						key={selected.id}
						criteria={criteria}
						edits={edits}
						flash={focusMatches(target, "persona", `${selected.id}`)}
						persona={selected}
						onDelete={setDeleting}
						onFlashDone={clearFocus}
						{...cellActions}
					/>
				)}
			</SettingsBox>

			{deleting && (
				<DeleteRowModal
					body={t("leadScoring.personas.deleteBody", {
						segments: edits.segments.filter(
							(segment) => segment.active && edits.isLinked(segment, deleting)
						).length,
					})}
					title={t("leadScoring.personas.deleteTitle", {
						name: deleting.name || t("leadScoring.shell.untitled"),
					})}
					onClose={() => setDeleting(null)}
					onDeactivate={() => {
						edits.updatePersona(deleting.id, { active: false });
						setDeleting(null);
					}}
					onDelete={() => {
						edits.removePersona(deleting.id);
						setSelectedId(null);
						setDeleting(null);
					}}
				/>
			)}

			{testing && <PersonaTestModal onClose={() => setTesting(false)} />}

			{syncChanges.length > 0 && (
				<ModalSegmentSync changes={syncChanges} isOpened onClose={() => setSyncChanges([])} />
			)}

			<ScoreSaveBar
				dirty={edits.dirty}
				error={edits.error}
				saving={edits.saving}
				onDiscard={edits.discard}
				onSave={edits.save}
			/>
			<ScoreLeaveGuard when={edits.dirty} onDiscard={edits.discard} onSave={edits.save} />
		</ScoreShell>
	);
};
